/**
 * Bulk action confirmation modals for the PO Lines table.
 *
 * Extracted from POLinesTab.tsx (CR-5972) so the tab file stays under the
 * 500-line limit and so the new Bulk Propose Change modal can live alongside
 * its siblings. The accept modal is kept inline in the tab because it has
 * no inputs — these three carry form state worth isolating.
 */
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { ReasonCodeSelect, getEffectiveReason } from '../../AcknowledgmentWorkflowModals';

interface BulkRejectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  rejecting: boolean;
  onReject: (reason: string) => void;
}

export function BulkRejectModal({ open, onOpenChange, count, rejecting, onReject }: BulkRejectModalProps) {
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const effectiveReason = getEffectiveReason(reasonSelect, customReason);

  const handleReject = () => {
    if (!effectiveReason) return;
    onReject(effectiveReason);
  };

  useEffect(() => {
    if (!open) {
      setReasonSelect('');
      setCustomReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Selected Lines</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm bulk rejection of selected PO lines
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to reject {count} selected line{count > 1 ? 's' : ''}. Please select a reason.
          </p>
          <ReasonCodeSelect
            value={reasonSelect}
            onChange={setReasonSelect}
            customValue={customReason}
            onCustomChange={setCustomReason}
            required
            label="Reason for Rejection"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            disabled={rejecting || !effectiveReason}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {rejecting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</>
            ) : (
              `Reject ${count} Line${count > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BulkHoldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  holding: boolean;
  onHold: (reason: string) => void;
}

export function BulkHoldModal({ open, onOpenChange, count, holding, onHold }: BulkHoldModalProps) {
  const [reason, setReason] = useState('');

  const handleHold = () => {
    if (!reason.trim()) return;
    onHold(reason.trim());
  };

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put Selected Lines On Hold</DialogTitle>
          <DialogDescription className="sr-only">
            Put selected PO lines on hold with a reason
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to put {count} selected line{count > 1 ? 's' : ''} on hold. The same reason will be applied to all lines.
          </p>
          <div className="space-y-2">
            <Label htmlFor="bulk-hold-reason">Hold Reason</Label>
            <Textarea
              id="bulk-hold-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for putting lines on hold..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleHold}
            disabled={holding || !reason.trim()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {holding ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Putting on hold...</>
            ) : (
              `Put ${count} Line${count > 1 ? 's' : ''} On Hold`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BulkProposeChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  proposing: boolean;
  onPropose: (data: { proposedDate: string; reason: string; comments?: string }) => void;
}

/**
 * CR-5972: Bulk propose a new delivery date across the selected lines.
 *
 * The per-line propose-change flow lets a user nudge the date, the quantity,
 * or the unit price — and they always edit each leg independently. Doing the
 * same as a bulk action would mean three optional inputs that fan-out across
 * dozens of lines, which is far too easy to misfire. The bulk modal is kept
 * intentionally narrow: one proposed date, one reason, applied uniformly.
 * Suppliers who need to vary quantity or price must do that per-line via the
 * detail modal.
 */
export function BulkProposeChangeModal({
  open,
  onOpenChange,
  count,
  proposing,
  onPropose,
}: BulkProposeChangeModalProps) {
  const [proposedDate, setProposedDate] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');

  const effectiveReason = getEffectiveReason(reasonSelect, customReason);

  const handleSubmit = () => {
    if (!proposedDate || !effectiveReason) return;
    onPropose({ proposedDate, reason: effectiveReason, comments: comments.trim() || undefined });
  };

  useEffect(() => {
    if (!open) {
      setProposedDate('');
      setReasonSelect('');
      setCustomReason('');
      setComments('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Propose Change on Selected Lines</DialogTitle>
          <DialogDescription className="sr-only">
            Propose a new delivery date on selected PO lines
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to propose a new delivery date on {count} selected line{count > 1 ? 's' : ''}.
            The same date and reason will be applied to all lines. To vary by line, action them individually instead.
          </p>
          <div className="space-y-2">
            <Label htmlFor="bulk-propose-date">Proposed Delivery Date</Label>
            <Input
              id="bulk-propose-date"
              type="date"
              value={proposedDate}
              onChange={(e) => setProposedDate(e.target.value)}
            />
          </div>
          <ReasonCodeSelect
            value={reasonSelect}
            onChange={setReasonSelect}
            customValue={customReason}
            onCustomChange={setCustomReason}
            required
            label="Reason for Change"
          />
          <div className="space-y-2">
            <Label htmlFor="bulk-propose-comments">Comments (optional)</Label>
            <Textarea
              id="bulk-propose-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Additional context for the customer..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={proposing || !proposedDate || !effectiveReason}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {proposing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Proposing...</>
            ) : (
              `Propose Change on ${count} Line${count > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
